import React, { useState, useEffect } from 'react';
import './AgencyDashboardPage.css';
import { getTrips } from '../../services/tripsService';
import { getVehicles } from '../../services/vehiclesService';
import { getDrivers } from '../../services/driversService';
import { getExpiringDriverDocuments } from '../../services/driverDocumentsService';
import { getBookingRequests } from '../../services/bookingRequestsService';
import api from '../../config/api';

const AgencyDashboardPage = () => {
  const [totalTripsToday, setTotalTripsToday] = useState(0);
  const [vehiclesAvailable, setVehiclesAvailable] = useState(0);
  const [driversOnTrip, setDriversOnTrip] = useState(0);
  const [expiringDocuments, setExpiringDocuments] = useState(0);
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentAgencyId, setCurrentAgencyId] = useState(null);

  // Get current agency ID from localStorage
  useEffect(() => {
    const loadAgencyInfo = async () => {
      let agency = null;
      const savedAgency = localStorage.getItem('agencyProfile');
      if (savedAgency) {
        try {
          agency = JSON.parse(savedAgency);
        } catch (error) {
          console.error('Error parsing agencyProfile:', error);
        }
      }
      
      // If not found, try userData
      if (!agency) {
        const userData = localStorage.getItem('userData');
        if (userData) {
          try {
            const user = JSON.parse(userData);
            if (user.agency_id || user.agency) {
              agency = {
                id: user.agency_id || user.agency?.id,
                agency_id: user.agency_id || user.agency?.id
              };
            }
          } catch (error) {
            console.error('Error parsing userData:', error);
          }
        }
      }
      
      if (agency) {
        const agencyId = agency.id || agency.agency_id || null;
        setCurrentAgencyId(agencyId);
      }
    };

    loadAgencyInfo();
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!currentAgencyId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Get today's date range (start and end of today)
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
        const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);

        // Fetch all metrics in parallel
        const [tripsResponse, allVehiclesResponse, driversResponse, expiringDocsResponse, activeTripsResponse] = await Promise.all([
          // 1. Total Trips Today (filtered by agency)
          getTrips({
            agency_id: currentAgencyId,
            start_date: startOfToday.toISOString(),
            end_date: endOfToday.toISOString(),
            limit: 100, // API max limit is 100
            offset: 0
          }),
          // 2. All Vehicles (filtered by agency)
          getVehicles({
            page: 1,
            limit: 100, // API max limit is 100
            agency_id: currentAgencyId
          }),
          // 3. Drivers (filtered by agency)
          getDrivers({
            page: 1,
            limit: 100, // API max limit is 100
            agency_id: currentAgencyId
          }),
          // 4. Expiring Documents
          getExpiringDriverDocuments(),
          // 5. Active Trips (for calculating vehicles available and drivers on trip)
          getTrips({
            agency_id: currentAgencyId,
            trip_status: 'In Progress',
            limit: 100, // API max limit is 100
            offset: 0
          })
        ]);

        // Process Total Trips Today
        let tripsCount = 0;
        if (tripsResponse?.data) {
          if (Array.isArray(tripsResponse.data)) {
            tripsCount = tripsResponse.data.length;
          } else if (tripsResponse.data.items) {
            tripsCount = tripsResponse.data.items.length;
          } else if (tripsResponse.data.total !== undefined) {
            tripsCount = tripsResponse.data.total;
          }
        } else if (Array.isArray(tripsResponse)) {
          tripsCount = tripsResponse.length;
        } else if (tripsResponse?.items) {
          tripsCount = tripsResponse.items.length;
        } else if (tripsResponse?.total !== undefined) {
          tripsCount = tripsResponse.total;
        }
        setTotalTripsToday(tripsCount);

        // Process Vehicles Available
        let allVehicles = [];
        if (allVehiclesResponse?.data) {
          if (Array.isArray(allVehiclesResponse.data)) {
            allVehicles = allVehiclesResponse.data;
          } else if (allVehiclesResponse.data.items) {
            allVehicles = allVehiclesResponse.data.items;
          }
        } else if (Array.isArray(allVehiclesResponse)) {
          allVehicles = allVehiclesResponse;
        } else if (allVehiclesResponse?.items) {
          allVehicles = allVehiclesResponse.items;
        }

        // Process active trips for both vehicles and drivers
        let activeTrips = [];
        if (activeTripsResponse?.data) {
          if (Array.isArray(activeTripsResponse.data)) {
            activeTrips = activeTripsResponse.data;
          } else if (activeTripsResponse.data.items) {
            activeTrips = activeTripsResponse.data.items;
          }
        } else if (Array.isArray(activeTripsResponse)) {
          activeTrips = activeTripsResponse;
        } else if (activeTripsResponse?.items) {
          activeTrips = activeTripsResponse.items;
        }

        // Get vehicles currently on active trips
        const vehiclesOnActiveTrips = new Set();
        activeTrips.forEach(trip => {
          if (trip.vehicle_id) {
            vehiclesOnActiveTrips.add(trip.vehicle_id);
          } else if (trip.vehicle?.id) {
            vehiclesOnActiveTrips.add(trip.vehicle.id);
          }
        });

        // Count vehicles that are not on active trips and have status 'Active' or 'Available'
        const availableVehicles = allVehicles.filter(vehicle => {
          const vehicleId = vehicle.id || vehicle.vehicle_id;
          const isOnTrip = vehiclesOnActiveTrips.has(vehicleId);
          const status = vehicle.status || vehicle.vehicle_status || '';
          return !isOnTrip && (status === 'Active' || status === 'Available');
        });

        setVehiclesAvailable(availableVehicles.length);

        // Process Drivers On Trip
        // Count unique drivers on active trips
        const driversOnActiveTrips = new Set();
        activeTrips.forEach(trip => {
          if (trip.assigned_driver_id) {
            driversOnActiveTrips.add(trip.assigned_driver_id);
          } else if (trip.assigned_driver?.id) {
            driversOnActiveTrips.add(trip.assigned_driver.id);
          } else if (trip.driver_id) {
            driversOnActiveTrips.add(trip.driver_id);
          }
        });
        setDriversOnTrip(driversOnActiveTrips.size);

        // Process Expiring Documents
        let expiringDocsCount = 0;
        if (expiringDocsResponse) {
          if (Array.isArray(expiringDocsResponse)) {
            expiringDocsCount = expiringDocsResponse.length;
          } else if (expiringDocsResponse.data) {
            if (Array.isArray(expiringDocsResponse.data)) {
              expiringDocsCount = expiringDocsResponse.data.length;
            } else if (expiringDocsResponse.data.items) {
              expiringDocsCount = expiringDocsResponse.data.items.length;
            } else if (expiringDocsResponse.data.count !== undefined) {
              expiringDocsCount = expiringDocsResponse.data.count;
            }
          } else if (expiringDocsResponse.count !== undefined) {
            expiringDocsCount = expiringDocsResponse.count;
          }
        }
        setExpiringDocuments(expiringDocsCount);

        // Fetch Recent Activity
        const activities = [];
        
        // 1. Get recent completed trips
        const completedTripsResponse = await getTrips({
          agency_id: currentAgencyId,
          trip_status: 'Completed',
          limit: 5,
          offset: 0,
          order_by: 'updated_at',
          sort_order: 'desc'
        });

        let completedTrips = [];
        if (completedTripsResponse?.data) {
          if (Array.isArray(completedTripsResponse.data)) {
            completedTrips = completedTripsResponse.data;
          } else if (completedTripsResponse.data.items) {
            completedTrips = completedTripsResponse.data.items;
          }
        } else if (Array.isArray(completedTripsResponse)) {
          completedTrips = completedTripsResponse;
        } else if (completedTripsResponse?.items) {
          completedTrips = completedTripsResponse.items;
        }

        completedTrips.forEach(trip => {
          const tripId = trip.id || trip.trip_id || '';
          const updatedAt = trip.updated_at || trip.completed_at || trip.created_at;
          if (updatedAt) {
            activities.push({
              type: 'trip_completed',
              icon: '✓',
              message: `Trip #${tripId} completed successfully`,
              timestamp: new Date(updatedAt),
              data: trip
            });
          }
        });

        // 2. Get recent booking requests
        const bookingRequestsResponse = await getBookingRequests({
          page: 1,
          limit: 5
        });

        let bookingRequests = [];
        if (bookingRequestsResponse?.data) {
          if (Array.isArray(bookingRequestsResponse.data)) {
            bookingRequests = bookingRequestsResponse.data;
          } else if (bookingRequestsResponse.data.items) {
            bookingRequests = bookingRequestsResponse.data.items;
          }
        } else if (Array.isArray(bookingRequestsResponse)) {
          bookingRequests = bookingRequestsResponse;
        } else if (bookingRequestsResponse?.items) {
          bookingRequests = bookingRequestsResponse.items;
        }

        bookingRequests.forEach(request => {
          const createdAt = request.created_at || request.requested_date;
          if (createdAt) {
            activities.push({
              type: 'booking_request',
              icon: '📋',
              message: 'New booking request received',
              timestamp: new Date(createdAt),
              data: request
            });
          }
        });

        // 3. Get recent payments (from bills)
        const billsResponse = await api.get('/api/bills', {
          params: {
            agency_id: currentAgencyId,
            payment_status: 'Paid',
            limit: 5,
            offset: 0,
            order_by: 'updated_at',
            sort_order: 'desc'
          }
        });

        let bills = [];
        if (billsResponse?.data?.success && billsResponse.data.data?.items) {
          bills = billsResponse.data.data.items;
        } else if (Array.isArray(billsResponse?.data)) {
          bills = billsResponse.data;
        }

        bills.forEach(bill => {
          const updatedAt = bill.updated_at || bill.paid_at || bill.bill_date;
          const amount = bill.total_amount || 0;
          if (updatedAt) {
            activities.push({
              type: 'payment',
              icon: '💳',
              message: `Payment received: ₹${amount.toLocaleString('en-IN')}`,
              timestamp: new Date(updatedAt),
              data: bill
            });
          }
        });

        // Sort activities by timestamp (most recent first) and take top 3
        activities.sort((a, b) => b.timestamp - a.timestamp);
        setRecentActivities(activities.slice(0, 3));

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        // Set defaults on error
        setTotalTripsToday(0);
        setVehiclesAvailable(0);
        setDriversOnTrip(0);
        setExpiringDocuments(0);
        setRecentActivities([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [currentAgencyId]);

  // Format time ago
  const formatTimeAgo = (date) => {
    if (!date) return '';
    const now = new Date();
    const diffMs = now - new Date(date);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else {
      return new Date(date).toLocaleDateString();
    }
  };

  return (
    <div className="agency-dashboard-content">
      {/* Key Metrics Cards */}
      <div className="agency-metrics-grid">
        <div className="agency-metric-card blue">
          <div className="agency-metric-icon">🚚</div>
          <div className="agency-metric-content">
            <div className="agency-metric-value">{loading ? '...' : totalTripsToday}</div>
            <div className="agency-metric-label">Total Trips Today</div>
            <div className="agency-metric-change positive">↑ Today</div>
          </div>
        </div>

        <div className="agency-metric-card green">
          <div className="agency-metric-icon">🚗</div>
          <div className="agency-metric-content">
            <div className="agency-metric-value">{loading ? '...' : vehiclesAvailable}</div>
            <div className="agency-metric-label">Vehicles Available</div>
            <div className="agency-metric-change positive">Available</div>
          </div>
        </div>

        <div className="agency-metric-card purple">
          <div className="agency-metric-icon">👤</div>
          <div className="agency-metric-content">
            <div className="agency-metric-value">{loading ? '...' : driversOnTrip}</div>
            <div className="agency-metric-label">Drivers On Trip</div>
            <div className="agency-metric-change positive">Active</div>
          </div>
        </div>

        <div className="agency-metric-card red">
          <div className="agency-metric-icon">⚠️</div>
          <div className="agency-metric-content">
            <div className="agency-metric-value">{loading ? '...' : expiringDocuments}</div>
            <div className="agency-metric-label">Expiring Documents</div>
            <div className="agency-metric-change positive">Expiring Soon</div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="agency-recent-activity">
        <h2 className="agency-section-title">Recent Activity</h2>
        <div className="agency-activity-list">
          {loading ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>Loading activities...</div>
          ) : recentActivities.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>No recent activities</div>
          ) : (
            recentActivities.map((activity, index) => (
              <div key={index} className="agency-activity-item">
                <span className="agency-activity-icon">{activity.icon}</span>
                <div className="agency-activity-content">
                  <p>{activity.message}</p>
                  <span className="agency-activity-time">{formatTimeAgo(activity.timestamp)}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AgencyDashboardPage;

